import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';

interface AudioPlayerProps {
  audioUrl: string;
  fileName?: string;
  className?: string;
  compact?: boolean;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ audioUrl, fileName, className = '', compact = false }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [isDragging, setIsDragging] = useState(false);

  // 初始化音频
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleTimeUpdate = () => {
      if (!isDragging) {
        setCurrentTime(audio.currentTime);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [isDragging]);

  // 播放/暂停
  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  // 快退15秒
  const skipBackward = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.max(0, audio.currentTime - 15);
  };

  // 快进15秒
  const skipForward = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.min(audio.duration, audio.currentTime + 15);
  };

  // 切换倍速
  const togglePlaybackRate = () => {
    const rates = [1.0, 1.25, 1.5, 1.75, 2.0];
    const currentIndex = rates.indexOf(playbackRate);
    const nextRate = rates[(currentIndex + 1) % rates.length];
    setPlaybackRate(nextRate);
    if (audioRef.current) {
      audioRef.current.playbackRate = nextRate;
    }
  };

  // 进度条拖动
  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    setCurrentTime(newTime);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  };

  // 格式化时间
  const formatTime = (seconds: number) => {
    if (isNaN(seconds)) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`${compact ? '' : 'bg-white rounded-2xl p-5'} ${className}`}
      style={compact ? {} : {
        boxShadow: '0px 4px 20px 0px rgba(116, 97, 238, 0.1)'
      }}
    >
      <audio ref={audioRef} src={audioUrl} preload="metadata" />

      {/* 文件名 */}
      {fileName && !compact && (
        <div className='text-[#303133] text-sm font-medium mb-3'>
          {fileName}
        </div>
      )}

      {/* Compact模式 - 简化版播放器 */}
      {compact ? (
        <div className='bg-[rgba(255,255,255,0.8)] rounded-[18px] h-[30px] flex items-center px-[12px] gap-[10px]'>
          {/* 播放按钮 */}
          <button
            onClick={togglePlay}
            className='flex-shrink-0 w-4 h-4 flex items-center justify-center'
          >
            {isPlaying ? (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="4" y="3" width="2.5" height="10" fill="#998dff" rx="0.5"/>
                <rect x="9.5" y="3" width="2.5" height="10" fill="#998dff" rx="0.5"/>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M5 3l8 5-8 5V3z" fill="#998dff"/>
              </svg>
            )}
          </button>

          {/* 进度条 */}
          <div className='flex-1 relative h-[6px] bg-[#e5e6eb] rounded-[45px] overflow-hidden'>
            <div
              className='absolute left-0 top-0 h-full bg-[#998dff] rounded-[45px] transition-all'
              style={{ width: `${(currentTime / duration) * 100}%` }}
            ></div>
            <input
              type='range'
              min={0}
              max={duration || 0}
              value={currentTime}
              onChange={handleProgressChange}
              onMouseDown={() => setIsDragging(true)}
              onMouseUp={() => setIsDragging(false)}
              onTouchStart={() => setIsDragging(true)}
              onTouchEnd={() => setIsDragging(false)}
              className='absolute inset-0 w-full h-full opacity-0 cursor-pointer'
            />
            {/* 进度点 */}
            <div
              className='absolute top-1/2 -translate-y-1/2 w-[12px] h-[12px] bg-white rounded-full shadow-[0px_0px_4px_rgba(0,0,0,0.2)] transition-all'
              style={{ left: `calc(${(currentTime / duration) * 100}% - 6px)` }}
            ></div>
          </div>

          {/* 时间显示 */}
          <div className='flex-shrink-0 text-[#0a1b39] text-[12px] leading-[18px] text-center min-w-[60px]'>
            <span className='text-[#0a1b39]'>{formatTime(currentTime)}</span>
            <span className='text-[#86909c]'>/{formatTime(duration)}</span>
          </div>
        </div>
      ) : (
        <>
          {/* 进度条 */}
          <div className='flex items-center gap-3 mb-4'>
            <span className='text-[#909399] text-xs min-w-[50px]'>
              {formatTime(currentTime)}
            </span>
            <div className='flex-1 relative'>
              <input
                type='range'
                min={0}
                max={duration || 0}
                value={currentTime}
                onChange={handleProgressChange}
                onMouseDown={() => setIsDragging(true)}
                onMouseUp={() => setIsDragging(false)}
                onTouchStart={() => setIsDragging(true)}
                onTouchEnd={() => setIsDragging(false)}
                className='w-full h-2 bg-[#e5e7eb] rounded-full appearance-none cursor-pointer audio-progress'
                style={{
                  background: `linear-gradient(to right, #7461ee 0%, #7461ee ${(currentTime / duration) * 100}%, #e5e7eb ${(currentTime / duration) * 100}%, #e5e7eb 100%)`
                }}
              />
            </div>
            <span className='text-[#909399] text-xs min-w-[50px] text-right'>
              {formatTime(duration)}
            </span>
          </div>

          {/* 控制按钮 */}
          <div className='flex items-center justify-center gap-6'>
            {/* 音频设置（占位） */}
            <button className='text-[#909399] hover:text-[#7461ee] transition-colors'>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M3 17v2h6v-2H3zM3 5v2h10V5H3zm10 16v-2h8v-2h-8v-2h-2v6h2zM7 9v2H3v2h4v2h2V9H7zm14 4v-2H11v2h10zm-6-4h2V7h4V5h-4V3h-2v6z" fill="currentColor"/>
              </svg>
            </button>

            {/* 后退15秒 */}
            <button
              onClick={skipBackward}
              className='text-[#606266] hover:text-[#7461ee] transition-colors'
            >
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <circle cx="16" cy="16" r="15" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M16 10v6l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M12 16l-3-3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <text x="16" y="20" fontSize="8" fill="currentColor" textAnchor="middle">15</text>
              </svg>
            </button>

            {/* 播放/暂停 */}
            <button
              onClick={togglePlay}
              className='w-14 h-14 rounded-full bg-gradient-to-br from-[#a782ff] to-[#828eff] flex items-center justify-center hover:shadow-lg transition-shadow'
            >
              {isPlaying ? (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <rect x="5" y="4" width="3" height="12" fill="white" rx="1"/>
                  <rect x="12" y="4" width="3" height="12" fill="white" rx="1"/>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M6 4l10 6-10 6V4z" fill="white"/>
                </svg>
              )}
            </button>

            {/* 前进15秒 */}
            <button
              onClick={skipForward}
              className='text-[#606266] hover:text-[#7461ee] transition-colors'
            >
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <circle cx="16" cy="16" r="15" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M16 10v6l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M20 16l3-3-3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <text x="16" y="20" fontSize="8" fill="currentColor" textAnchor="middle">15</text>
              </svg>
            </button>

            {/* 倍速 */}
            <button
              onClick={togglePlaybackRate}
              className='text-[#606266] hover:text-[#7461ee] transition-colors min-w-[48px] text-center'
            >
              <span className='text-sm font-medium'>{playbackRate}x</span>
            </button>
          </div>
        </>
      )}

      {/* 样式 */}
      <style>{`
        .audio-progress::-webkit-slider-thumb {
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #7461ee;
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .audio-progress::-moz-range-thumb {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #7461ee;
          cursor: pointer;
          border: none;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
      `}</style>
    </motion.div>
  );
};

export default AudioPlayer;


